<template>
  <div>
    <data-table
      :key="`${tableKey}-${isCheckable}`"
      :tableOpts="tableOpts"
      :loadData="loadData"
      :apiMode="true"
      @cell-clicked="onCellClicked"
    />

    <list-item-form
      v-if="showModal"
      :item="itemToEdit"
      :config="modalConfig"
      @close="closeModal"
      @refresh-table="refreshTable"
    />
  </div>
</template>

<script>
import { reactive, toRefs } from 'vue';
import { useStore } from 'vuex';
import { cloneDeep, get, trim, find } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import DataTable from '@/components/table-fields/DataTable.vue';
import ListItemForm from '@/components/modals/ListItemForm.vue';
import Companies from '@/models/Companies';
import Projects from '@/models/Projects';

export default {
  name: 'WorkSteps',
  props: {
    searchText: {
      type: String,
      default: '',
    },
  },

  components: {
    'data-table': DataTable,
    'list-item-form': ListItemForm,
  },

  setup(props) {
    const store = useStore();
    const toast = useToast();

    const data = reactive({
      tableKey: 0,
      showModal: false,
      itemToEdit: {},
    });

    const getCompany = async () => {
      await store.getters.userPromise;
      const user = store.state.userData;
      return Companies.getOne(user.company);
    };

    const saveCompany = async (company) => {
      await Companies.save(company);
      store.dispatch('getCompanyData');
      store.commit('setCompanyRuns', []);
      await store.dispatch('getCompanyRuns');
    };

    const modalConfig = {
      label: 'Work Step',
      showSubCategories: false,
      showColor: false,
      saveFn: async (payload) => {
        const company = await getCompany();
        const name = trim(payload.name);

        if (name.length < 3) {
          return { failedData: [{ _errorMsg: 'Work Step name should be at least 3 characters long!' }] };
        }
        if (find(company.companyRuns, (r) => r.name && r.name.trim() === name)) {
          return { failedData: [{ _errorMsg: `Work Step "${name}" already exists!` }] };
        }

        company.companyRuns.push({ name, text: name });
        await saveCompany(company);
        return { data: [{ name }] };
      },
      updateFn: async (payload) => {
        const company = await getCompany();
        const name = trim(payload.name);

        if (name.length < 3) {
          throw new Error('Work Step name should be at least 3 characters long!');
        }
        const duplicate = find(
          company.companyRuns,
          (r) => r.name && r.name.trim() === name && r._id !== payload._id,
        );
        if (duplicate) {
          throw new Error(`Work Step "${name}" already exists!`);
        }

        const oldRun = find(company.companyRuns, (r) => r._id === payload._id);
        if (!oldRun) {
          throw new Error('Work Step not found');
        }

        await Companies.reNameRun({
          oldRun,
          type: 'companyRun',
          runType: 'projectCompanyRuns',
          newName: name,
        });
        store.dispatch('getCompanyData');
        store.commit('setCompanyRuns', []);
        await store.dispatch('getCompanyRuns');
      },
    };

    const tableOpts = {
      defaultSortField: 'name',
      defaultSortOrder: 'asc',
      stickeyHeaders: false,
      fields: [
        {
          title: 'Name',
          id: 'name',
          prop: 'name',
          name: 'field-generic-input',
          width: '400px',
          visible: true,
          alwaysVisible: true,
          inputProps: {
            type: 'text',
            openCard: false,
          },
        },
        {
          title: 'ACTIONS',
          name: 'table-field-actions',
          id: 'action',
          hideFromChooser: true,
          alwaysVisible: true,
          defaultVisible: true,
          width: '112px',
          permissionModule: 'app&projects',
          permissionKey: 'company_lists',
          inputProps: {
            showEdit: false,
            groupButtons() {
              return [
                {
                  icon: 'icon-edit',
                  event: 'edit',
                  tooltip: 'Edit Work Step',
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
                {
                  icon: 'icon-bin',
                  event: 'delete',
                  tooltip: 'Delete Work Step',
                  disabled: (row) => row.isUsed,
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
              ];
            },
          },
        },
      ],
    };

    const loadData = async (params) => {
      const company = await getCompany();
      const getUsedRuns = await Projects.getUsedRuns({
        type: 'companyRuns',
        runType: 'companyRuns',
      });
      let runs = (company.companyRuns || []).map((run) => ({
        ...run,
        isUsed: getUsedRuns.includes(run.name),
      }));
      const search = props.searchText || '';
      if (search) {
        runs = runs.filter((r) =>
          r.name.toLowerCase().includes(search.toLowerCase()),
        );
      }
      return {
        data: runs,
        total: runs.length,
      };
    };

    const refreshTable = () => {
      data.tableKey += 1;
    };

    const openModal = (item = {}) => {
      data.itemToEdit = cloneDeep(item);
      data.showModal = true;
    };

    const closeModal = () => {
      data.showModal = false;
      data.itemToEdit = {};
    };

    const onCellClicked = ({ type, data: rowData }) => {
      if (type === 'edit') {
        openModal(rowData);
      } else if (type === 'delete') {
        if (rowData.isUsed) {
          toast.warning('This Work Step is in use and cannot be deleted');
          return;
        }

        DialogProgrammatic.confirm({
          title: 'Delete Work Step',
          message: `Are you sure you want to delete "${rowData.name}"?`,
          cancelButton: 'Cancel',
          type: 'danger',
          onClose: () => {
            data.isCheckable = false;
            data.selectedRows = [];
          },onConfirm: async () => {
            try {
              const company = await getCompany();

              if (company.companyRuns.length < 2) {
                DialogProgrammatic.confirm({
                  message: 'Cannot delete Work Step — at least one Work Step must remain!',
                  okButton: 'OK',
                });
                return;
              }
              _.remove(company.companyRuns, (r) => r._id === rowData._id);
              await saveCompany(company);
              toast.success('Work Step deleted successfully');
              refreshTable();
            } catch (e) {
              toast.error(get(e, 'data.message') || e.message || 'Error deleting Work Step');
            }
          },
        });
      }
    };

    return {
      ...toRefs(data),
      modalConfig,
      tableOpts,
      loadData,
      onCellClicked,
      refreshTable,
      openModal,
      closeModal,
    };
  },
};
</script>

<style scoped>
</style>